// Numeric count-up animation helper (W3.1 §5.5 + §5.6 segment 1 + 2).
//
// Pure function: takes a target number + duration + start time, returns the
// frame value at the queried elapsed time. Easing is `ease-letter` (the same
// Apple-style curve used by the tear-off letter animation). Respects
// `prefers-reduced-motion` via the `reduced` flag — when set, returns the
// final value immediately (single-frame jump).

import { useEffect, useState } from "react";

export interface CountUpOptions {
  /** Animation duration in ms (default 800 = `dur-letter`). */
  duration?: number;
  /** Start time in ms (epoch). Caller usually passes `Date.now()`. */
  start?: number;
  /** When `true`, return final value immediately (reduced-motion). */
  reduced?: boolean;
}

const DEFAULT_DURATION = 800; // dur-letter per spec §5.5

/**
 * Apple-style easing curve. Mirrors `var(--ease-letter)` in globals.css.
 * Tuned to start slow, accelerate in the middle, and decelerate into the
 * final value (the "settle" feel).
 */
export function easeLetter(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  // Cubic-bezier-like approximation; matches the cubic-bezier(.4, .0, .2, 1)
  // feel Apple uses for letter-settle animations.
  const inv = 1 - t;
  return 1 - inv * inv * inv;
}

/**
 * Compute the value at a given elapsed time.
 * Pure — no React, no DOM. The hook below wraps it for ergonomic use.
 */
export function countUpValueAt(
  target: number,
  elapsedMs: number,
  duration: number = DEFAULT_DURATION,
  reduced: boolean = false,
): number {
  if (reduced || duration <= 0) return target;
  if (elapsedMs <= 0) return 0;
  if (elapsedMs >= duration) return target;
  const t = elapsedMs / duration;
  return Math.round(easeLetter(t) * target);
}

/**
 * React hook: animates 0 → `target` over `duration` ms on mount. Honors
 * `prefers-reduced-motion` (returns target instantly when set).
 */
export function useCountUp(
  target: number,
  options: Omit<CountUpOptions, "target"> = {},
): number {
  const { duration = DEFAULT_DURATION, reduced = false } = options;
  const [value, setValue] = useState<number>(reduced ? target : 0);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return undefined;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= duration) {
        setValue(target);
        return;
      }
      setValue(countUpValueAt(target, elapsed, duration, reduced));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [target, duration, reduced]);

  return value;
}

/**
 * Detect `prefers-reduced-motion: reduce` once at mount. SSR-safe (returns
 * `false` during the first render, then updates).
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}